import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Barcode, CheckCircle, X, Camera } from "@phosphor-icons/react";
import { Html5Qrcode } from "html5-qrcode";
import useIsMobile from "@/hooks/useIsMobile";

/**
 * Scanner input for USB barcode scanners + camera (PWA).
 * USB scanners emulate keyboard input ending with Enter.
 * Camera mode uses html5-qrcode for real-time barcode decoding.
 *
 * Exposes an imperative handle so parents can programmatically re-open the
 * camera (e.g. advancing through a multi-step wizard without the operator
 * having to tap the camera icon between each product).
 */
const ScannerInput = forwardRef(function ScannerInput({
  onScan,
  autoFocus = true,
  placeholder = "Scansiona o digita seriale...",
  lastScanned = "",
  label = "Scanner",
  testid = "scanner-input",
}, ref) {
  const [value, setValue] = useState("");
  const [flash, setFlash] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraError, setCameraError] = useState("");
  const inputRef = useRef(null);
  const scannerRef = useRef(null);
  const scannerReaderId = useRef(`qr-reader-${testid}-${Math.random().toString(36).slice(2)}`).current;
  const isMobile = useIsMobile();

  useEffect(() => {
    if (autoFocus && inputRef.current && !cameraOpen) inputRef.current.focus();
  }, [autoFocus, cameraOpen]);

  const trigger = (code) => {
    onScan(code);
    setFlash(true);
    setTimeout(() => setFlash(false), 400);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === "Tab") {
      e.preventDefault();
      const code = value.trim();
      if (code) {
        trigger(code);
        setValue("");
        inputRef.current?.focus();
      }
    } else if (e.key === "Escape") {
      setValue("");
    }
  };

  const submitManual = () => {
    const code = value.trim();
    if (code) {
      trigger(code);
      setValue("");
      inputRef.current?.focus();
    }
  };

  const openCamera = async () => {
    // Request camera permission SYNCHRONOUSLY within the click event so Chrome
    // Android (and modern browsers) surface the permission prompt. Doing this
    // inside a useEffect after setState loses the user-gesture activation.
    setCameraError("");
    if (typeof window !== "undefined") {
      const isSecure = window.isSecureContext || window.location.hostname === "localhost";
      if (!isSecure) {
        setCameraError("La fotocamera funziona solo su HTTPS. Apri l'app da un URL https:// per usare la scansione ottica.");
        setCameraOpen(true);
        return;
      }
      if (!navigator?.mediaDevices?.getUserMedia) {
        setCameraError("Questo browser non supporta l'accesso alla fotocamera. Usa Chrome/Safari aggiornato.");
        setCameraOpen(true);
        return;
      }
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" } },
      });
      // Permission granted — stop the probe stream, html5-qrcode will open its own
      stream.getTracks().forEach((t) => t.stop());
      setCameraOpen(true);
    } catch (permErr) {
      let msg;
      const n = permErr?.name;
      if (n === "NotAllowedError" || n === "PermissionDeniedError") {
        msg = "Hai negato l'accesso alla fotocamera per questo sito. Tocca l'icona 🔒 nella barra URL, cambia il permesso Fotocamera su 'Consenti', poi ricarica la pagina.";
      } else if (n === "NotFoundError" || n === "DevicesNotFoundError") {
        msg = "Nessuna fotocamera trovata sul dispositivo.";
      } else if (n === "NotReadableError" || n === "TrackStartError") {
        msg = "La fotocamera è in uso da un'altra app. Chiudi le altre app che la usano e riprova.";
      } else if (n === "SecurityError") {
        msg = "Accesso alla fotocamera bloccato dal contesto di sicurezza.";
      } else {
        msg = permErr?.message || "Impossibile accedere alla fotocamera.";
      }
      setCameraError(msg);
      setCameraOpen(true); // Open modal to show the error UI
    }
  };

  // Expose imperative methods so parent components can drive the scanner
  // across multi-step wizards (e.g. re-open camera when advancing to the
  // next product in an ODT fulfilment flow).
  useImperativeHandle(ref, () => ({
    openCamera,
    isMobile,
  }), [isMobile]);

  // Camera scan setup — permission already granted at this point via openCamera()
  useEffect(() => {
    if (!cameraOpen || cameraError) return;
    let cancelled = false;

    (async () => {
      try {
        const html5 = new Html5Qrcode(scannerReaderId);
        scannerRef.current = html5;
        await html5.start(
          { facingMode: "environment" },
          { fps: 12, qrbox: { width: 280, height: 140 }, aspectRatio: 1.6 },
          (decodedText) => {
            if (cancelled) return;
            trigger(decodedText.trim());
            html5.stop().then(() => html5.clear()).catch(() => {});
            scannerRef.current = null;
            setCameraOpen(false);
          },
          () => { /* per-frame errors ignored */ }
        );
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Camera scanner start failed:", err);
        setCameraError(err?.message || "Impossibile avviare la scansione.");
      }
    })();

    return () => {
      cancelled = true;
      if (scannerRef.current) {
        try {
          scannerRef.current.stop().then(() => scannerRef.current.clear()).catch(() => {});
        } catch (err) { console.warn("Scanner cleanup error", err); }
        scannerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cameraOpen, scannerReaderId, cameraError]);

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Barcode size={18} weight="bold" className="text-[#002FA7]" />
        <span className="label-eyebrow">{label}</span>
        <span className="text-[10px] text-neutral-400 ml-auto hidden sm:inline">
          {isMobile ? "Fotocamera · Manuale" : "USB · Manuale"}
        </span>
      </div>

      <div className={`flex items-stretch border-2 transition-colors duration-150 ${flash ? "border-emerald-500 bg-emerald-50" : "border-[#002FA7] bg-white"}`}>
        <input
          ref={inputRef}
          data-testid={testid}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={() => { if (!cameraOpen) setTimeout(() => inputRef.current?.focus(), 100); }}
          placeholder={placeholder}
          className="flex-1 px-3 py-3 outline-none text-base font-mono bg-transparent min-w-0"
          autoComplete="off"
          spellCheck={false}
          inputMode="text"
        />
        {isMobile && (
          <button
            type="button"
            data-testid={`${testid}-camera`}
            onClick={openCamera}
            className="px-3 border-l border-[#002FA7]/30 hover:bg-[#002FA7]/5 transition-colors text-[#002FA7]"
            title="Scansiona con fotocamera"
          >
            <Camera size={20} weight="bold" />
          </button>
        )}
        <button
          type="button"
          data-testid={`${testid}-submit`}
          onClick={submitManual}
          className="px-4 bg-[#002FA7] text-white text-sm font-medium hover:bg-[#002480] transition-colors"
        >
          OK
        </button>
      </div>

      {lastScanned && (
        <div className="flex items-center gap-2 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 px-3 py-2">
          <CheckCircle size={14} weight="fill" />
          <span>Ultimo scan:</span>
          <span className="font-mono font-semibold">{lastScanned}</span>
        </div>
      )}

      {/* Camera modal */}
      {cameraOpen && (
        <div className="fixed inset-0 bg-black/90 z-[60] flex items-center justify-center p-4" data-testid={`${testid}-camera-modal`}>
          <div className="w-full max-w-md">
            <div className="flex items-center justify-between text-white mb-3">
              <div className="flex items-center gap-2">
                <Camera size={20} weight="bold" />
                <span className="font-display font-bold">Scanner Fotocamera</span>
              </div>
              <button
                data-testid={`${testid}-camera-close`}
                onClick={() => setCameraOpen(false)}
                className="p-2 hover:bg-white/10 transition-colors"
              >
                <X size={20} />
              </button>
            </div>
            <div id={scannerReaderId} className="bg-black overflow-hidden" style={{ minHeight: 250 }} />
            {cameraError ? (
              <div className="bg-red-100 border border-red-300 text-red-800 text-sm p-4 mt-3 space-y-2">
                <div className="font-semibold">⚠️ Fotocamera non disponibile</div>
                <div className="text-xs leading-relaxed">{cameraError}</div>
                <div className="text-xs text-red-700 border-t border-red-200 pt-2 mt-2">
                  <div className="font-semibold mb-1">Come sbloccare:</div>
                  <ul className="list-disc list-inside space-y-0.5">
                    <li><b>Chrome/Edge Android</b>: tocca l&apos;icona 🔒/ⓘ nella barra URL → Autorizzazioni sito → Fotocamera → Consenti</li>
                    <li><b>Safari iOS</b>: Impostazioni → Safari → Fotocamera → Consenti; se app è in home, Impostazioni → app → Fotocamera</li>
                    <li>Poi <b>ricarica la pagina</b> e riprova</li>
                  </ul>
                </div>
              </div>
            ) : (
              <div className="text-center text-white/70 text-xs mt-3">
                Inquadra il barcode. Assicurati di essere in un ambiente ben illuminato.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
});

export default ScannerInput;
